import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  CircularProgress,
  Box,
  Typography,
  Skeleton,
  TableSortLabel,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import dayjs from "dayjs";
import { BASE_URL } from "../constants/global";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";

interface AedesTableProps {
  searchTerm: string;
  startDate?: Date | null;
  endDate?: Date | null;
  siteIdFilter?: string;
  descriptorIdFilter?: number;
  collectionIds?: number[];
}

export const AedesTable: React.FC<AedesTableProps> = ({ 
  searchTerm, 
  startDate, 
  endDate, 
  siteIdFilter, 
  descriptorIdFilter, 
  collectionIds 
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>("id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: ['images', 'search', { searchTerm, startDate, endDate, siteIdFilter, descriptorIdFilter, collectionIds, page, pageSize, sortField, sortDirection }],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const url = `${BASE_URL}/v1/image/search`;
      
      const searchParams = {
        searchTerm: searchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        siteId: siteIdFilter || undefined,
        descriptorId: descriptorIdFilter || undefined,
        collectionIds: collectionIds && collectionIds.length > 0 ? collectionIds : undefined,
        page: page,
        size: pageSize,
        sortField: sortField,
        sortDirection: sortDirection,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(searchParams),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch images");
      }

      return response.json();
    },
  });

  const images = data?.content || [];
  const totalPages = data?.page?.totalPages || 0;
  const totalElements = data?.page?.totalElements || 0;
  const error = queryError ? "Error fetching images: " + (queryError as Error).message : "";

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage - 1);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = event.target.value as number;
    setPageSize(newSize);
    setPage(0); // Reset to the first page when the page size changes
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getModelType = (clientInfo: string | null) => {
    if (clientInfo === null) return "Unknown";
    if (clientInfo.includes("YOLO Cloud")) return "YOLO Cloud";
    if (clientInfo.includes("YOLO")) return "YOLO";
    if (clientInfo.includes("OpenCV")) return "OpenCV";
    if (clientInfo.includes("Offline")) return "YOLO Mobile Offline";
    if (clientInfo.includes("Mobile")) return "YOLO Mobile";
    return "Unknown";
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", pb: 2 }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><Skeleton width="80%" /></TableCell>
                <TableCell align="right"><Skeleton width="60%" /></TableCell>
                <TableCell align="right"><Skeleton width="60%" /></TableCell>
                <TableCell align="right"><Skeleton width="60%" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ pt: 3, textAlign: "center" }}>
        <h1>{error}</h1>
        <Button variant="outlined" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  if (images.length === 0 && !loading) {
    return (
      <Box sx={{ pt: 3, pb: 3, textAlign: "center" }}>
        <BugReportIcon sx={{ fontSize: 80, color: "#ccc", mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          No images found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {searchTerm || startDate || endDate || siteIdFilter || descriptorIdFilter || (collectionIds && collectionIds.length > 0)
            ? "Try adjusting your filters to see more results"
            : "Get started by uploading your first mosquito egg image"}
        </Typography>
        {!searchTerm && !startDate && !endDate && (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              startIcon={<BugReportIcon />}
              variant="contained"
              component={Link}
              to="/yolocloud"
            >
              Count Eggs (YOLO Cloud)
            </Button>
            <Button
              startIcon={<BugReportIcon />}
              variant="outlined"
              component={Link}
              to="/yolo"
            >
              Count Eggs (YOLO)
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", pb: 2 }}>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Showing {images.length} of {totalElements} results
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="image table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortField === "id"}
                  direction={sortField === "id" ? sortDirection : "desc"}
                  onClick={() => handleSort("id")}
                >
                  Image ID
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortField === "pictureDate"}
                  direction={sortField === "pictureDate" ? sortDirection : "desc"}
                  onClick={() => handleSort("pictureDate")}
                >
                  Date Captured
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                Model
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={sortField === "computedEggCount"}
                  direction={sortField === "computedEggCount" ? sortDirection : "desc"}
                  onClick={() => handleSort("computedEggCount")}
                >
                  Egg Count
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {images.map((row: any) => (
              <TableRow
                key={row.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  <Link to={`/image/${row.id}`}>{row.uniqueImageId}</Link>
                </TableCell>
                <TableCell align="right">
                  {dayjs(row.pictureDate).format("MM-DD-YYYY")}
                </TableCell>
                <TableCell align="right">
                  {getModelType(row?.clientInfo)}
                </TableCell>
                <TableCell align="right">
                  {row.computedEggCount ? (
                    row.computedEggCount
                  ) : (
                    <Chip label="no eggs" color="warning" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <InputLabel id="page-size-select-label">Per Page</InputLabel>
          <Select
            labelId="page-size-select-label"
            id="page-size-select"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Per Page"
            size="small"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default AedesTable;
